import { KubesoloHost } from '@api/types.gen';

/** Placeholder cap on machines per connection test, matching the API's. */
export const MAX_MACHINES = 500;

const DEFAULT_SSH_PORT = 22;
const MAX_PORT = 65535;
const MAX_OCTET = 255;
const OCTET_VALUES = 256;
const MAX_HOSTNAME_LENGTH = 253;
/** Errors shown at once, so a long paste doesn't flood the form. */
const MAX_ERRORS_SHOWN = 5;

const ipv4Pattern = /^\d+\.\d+\.\d+\.\d+$/;
const ipv6Pattern = /^[0-9a-f:.]+$/i;
const hostnameLabelPattern = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i;
const portPattern = /^\d+$/;
const separatorPattern = /[\s,;]+/;
// spaces around a range's hyphen belong to the range, not between entries
const spacedRangePattern = /(\d+\.\d+\.\d+\.\d+)\s*-\s*(\d+\.\d+\.\d+\.\d+)/g;
const rangePattern = /^(\d+\.\d+\.\d+\.\d+)-(\d+\.\d+\.\d+\.\d+)(?::(.*))?$/;
const cidrPattern = /^(\d+\.\d+\.\d+\.\d+)\/(\d+)(?::(.*))?$/;
const commentPattern = /#.*/;
// spreadsheets export cells in quotes
const quotesPattern = /^["']|["']$/g;
const IPV4_BITS = 32;
/** Blocks this small have no network and broadcast address to leave out. */
const SMALLEST_SUBNET_PREFIX = 31;
const NETWORK_AND_BROADCAST_ADDRESSES = 2;

type Host = Required<KubesoloHost>;

interface ParsedMachines {
  hosts: Array<Host>;
  errors: Array<string>;
}

type ParsedEntry = { hosts: Array<Host> } | { error: string };

/** Validates machines the way the API does, so the form rejects what the API would. */
export function parseMachines(text: string): ParsedMachines {
  const hosts: Array<Host> = [];
  const errors: Array<string> = [];
  const seen = new Set<string>();

  text.split('\n').forEach((line, index) => {
    const lineNumber = index + 1;
    const entries = line
      .replace(commentPattern, '')
      .replace(spacedRangePattern, '$1-$2')
      .split(separatorPattern)
      .map((entry) => entry.replace(quotesPattern, ''))
      .filter(Boolean);

    entries.forEach((entry) => {
      const parsed = parseEntry(entry);
      if ('error' in parsed) {
        errors.push(`Line ${lineNumber}: ${parsed.error}`);
        return;
      }

      parsed.hosts.forEach((host) => {
        const key = `${host.host}:${host.port}`;
        if (seen.has(key)) {
          errors.push(
            `Line ${lineNumber}: ${formatHost(host)} is listed more than once`
          );
          return;
        }

        seen.add(key);
        hosts.push(host);
      });
    });
  });

  if (hosts.length > MAX_MACHINES) {
    errors.push(
      `A test can hold at most ${MAX_MACHINES} machines, this has ${hosts.length}`
    );
  }

  return { hosts, errors };
}

export function formatErrors(errors: Array<string>) {
  const shown = errors.slice(0, MAX_ERRORS_SHOWN);
  const hidden = errors.length - shown.length;

  return hidden > 0
    ? [...shown, `and ${hidden} more`].join('\n')
    : shown.join('\n');
}

/** The machines one per line, the way the Machines field takes them. */
export function formatMachines(hosts: Array<Host>) {
  return hosts.map(formatHost).join('\n');
}

/** host, host:port, or [ipv6]:port, leaving out the default SSH port. */
export function formatHost({ host, port }: Host) {
  if (port === DEFAULT_SSH_PORT) {
    return host;
  }

  return host.includes(':') ? `[${host}]:${port}` : `${host}:${port}`;
}

function parseEntry(entry: string): ParsedEntry {
  if (entry.includes('://')) {
    return { error: `"${entry}" is a URL, enter only the host[:port]` };
  }

  if (entry.includes('@')) {
    return {
      error: `"${entry}" names a user, enter only the host[:port]. The SSH user comes from the credential`,
    };
  }

  const range = rangePattern.exec(entry);
  if (range) {
    return parseRange(entry, range[1], range[2], range[3]);
  }

  const cidr = cidrPattern.exec(entry);
  if (cidr) {
    return parseCidr(entry, cidr[1], Number(cidr[2]), cidr[3]);
  }

  const host = parseHost(entry);
  return host
    ? { hosts: [host] }
    : { error: `"${entry}" isn't a valid host[:port]` };
}

function parseRange(
  entry: string,
  first: string,
  last: string,
  portText?: string
): ParsedEntry {
  const port = parsePort(portText);
  const start = ipv4ToNumber(first);
  const end = ipv4ToNumber(last);
  if (port === null || start === null || end === null) {
    return { error: `"${entry}" isn't a valid IPv4 range` };
  }

  return expandAddresses(
    entry,
    Math.min(start, end),
    Math.abs(end - start) + 1,
    port
  );
}

/** A CIDR block's usable addresses, leaving out its network and broadcast address. */
function parseCidr(
  entry: string,
  address: string,
  prefix: number,
  portText?: string
): ParsedEntry {
  const port = parsePort(portText);
  const value = ipv4ToNumber(address);
  if (port === null || value === null || prefix > IPV4_BITS) {
    return { error: `"${entry}" isn't a valid IPv4 CIDR block` };
  }

  const blockSize = 2 ** (IPV4_BITS - prefix);
  const network = Math.floor(value / blockSize) * blockSize;
  if (prefix >= SMALLEST_SUBNET_PREFIX) {
    return expandAddresses(entry, network, blockSize, port);
  }

  return expandAddresses(
    entry,
    network + 1,
    blockSize - NETWORK_AND_BROADCAST_ADDRESSES,
    port
  );
}

function expandAddresses(
  entry: string,
  first: number,
  size: number,
  port: number
): ParsedEntry {
  // checked before expanding, so a huge range can't stall the form
  if (size > MAX_MACHINES) {
    return {
      error: `${entry} has ${size} addresses, a test can hold at most ${MAX_MACHINES} machines`,
    };
  }

  return {
    hosts: Array.from({ length: size }, (_, i) => ({
      host: numberToIpv4(first + i),
      port,
    })),
  };
}

function parseHost(entry: string): Host | null {
  const { host, port } = splitHostPort(entry);
  if (port === null) {
    return null;
  }

  const normalized = normalizeHost(host);
  return normalized ? { host: normalized, port } : null;
}

function splitHostPort(line: string): { host: string; port: number | null } {
  const bracketed = /^\[([^\]]+)\](?::(.*))?$/.exec(line);
  if (bracketed) {
    return { host: bracketed[1], port: parsePort(bracketed[2]) };
  }

  // more than one colon and no brackets is an IPv6 address without a port
  const colons = line.split(':').length - 1;
  if (colons !== 1) {
    return { host: line, port: DEFAULT_SSH_PORT };
  }

  const [host, port] = line.split(':');
  return { host, port: parsePort(port) };
}

function parsePort(port?: string) {
  if (port === undefined) {
    return DEFAULT_SSH_PORT;
  }

  const value = Number(port);
  return portPattern.test(port) && value >= 1 && value <= MAX_PORT
    ? value
    : null;
}

function normalizeHost(host: string) {
  if (ipv4Pattern.test(host)) {
    const value = ipv4ToNumber(host);
    return value === null ? null : numberToIpv4(value);
  }

  if (host.includes(':')) {
    return ipv6Pattern.test(host) ? normalizeIpv6(host) : null;
  }

  // a fully qualified name may end in a dot
  const name = host.endsWith('.') ? host.slice(0, -1) : host;
  return isValidHostname(name) ? name.toLowerCase() : null;
}

/** The URL parser validates the address and writes it in its shortest form, as the API does. */
function normalizeIpv6(address: string) {
  try {
    return new URL(`http://[${address}]`).hostname.slice(1, -1);
  } catch {
    return null;
  }
}

function ipv4ToNumber(address: string) {
  const octets = address.split('.').map(Number);
  if (!ipv4Pattern.test(address) || octets.some((o) => o > MAX_OCTET)) {
    return null;
  }

  return octets.reduce((value, octet) => value * OCTET_VALUES + octet, 0);
}

function numberToIpv4(value: number) {
  return [3, 2, 1, 0]
    .map((shift) => Math.floor(value / OCTET_VALUES ** shift) % OCTET_VALUES)
    .join('.');
}

/** An all-numeric last label would be a malformed address, not a name. */
function isValidHostname(host: string) {
  const labels = host.split('.');

  return (
    host.length <= MAX_HOSTNAME_LENGTH &&
    labels.every((label) => hostnameLabelPattern.test(label)) &&
    !/^\d+$/.test(labels[labels.length - 1])
  );
}
